export const VETCAN_STUDIO_STEPS = [
  "describe_assistant",
  "persona",
  "capabilities",
  "transfer_rules",
  "booking_behavior",
  "preview",
  "deploy_readiness",
] as const;

export type VetCanStudioStepId = (typeof VETCAN_STUDIO_STEPS)[number];

export type VetCanStudioStepState = "ready" | "draft" | "blocked";
export type VetCanStudioChecklistState = "done" | "review" | "blocked";

export type VetCanStudioField = {
  label: string;
  value: string;
};

export type VetCanStudioChecklistItem = {
  label: string;
  state: VetCanStudioChecklistState;
};

export type VetCanStudioStep = {
  id: VetCanStudioStepId;
  title: string;
  eyebrow: string;
  summary: string;
  state: VetCanStudioStepState;
  fields: VetCanStudioField[];
  checklist: VetCanStudioChecklistItem[];
  callout?: {
    title: string;
    body: string;
  };
};

export type VetCanStudioStatusCard = {
  label: string;
  state: VetCanStudioStepState;
  value: string;
  detail: string;
};

export type VetCanStudioReadModel = {
  version: "a24.6_v1";
  title: string;
  subtitle: string;
  assistant: {
    name: string;
    lane: string;
    operatorMode: string;
    summary: string;
  };
  stepOrder: VetCanStudioStepId[];
  steps: VetCanStudioStep[];
  controls: VetCanStudioStatusCard[];
  liveDeployment: {
    state: "blocked";
    headline: string;
    summary: string;
    blockers: string[];
  };
  routingTruth: {
    title: string;
    summary: string;
    facts: string[];
  };
  previewLane: {
    title: string;
    summary: string;
    channels: string[];
  };
  businessUserExperience: {
    title: string;
    summary: string;
    visibleNow: string[];
    gatedNow: string[];
    nextMove: string[];
  };
  launchCandidateCloseout: {
    title: string;
    summary: string;
    scoreLabel: string;
    scoreValue: string;
    scoreDetail: string;
    proofSummary: string[];
    knownBlockers: string[];
    nextOperatorWorkflow: string[];
  };
};

export function getVetCanStudioReadModel(): VetCanStudioReadModel {
  const steps: VetCanStudioStep[] = [
    {
      id: "describe_assistant",
      title: "Describe assistant",
      eyebrow: "Builder brief",
      summary:
        "Capture the operator-facing brief for a premium AI front-desk assistant without changing runtime routing.",
      state: "ready",
      fields: [
        { label: "Assistant name", value: "VetCan Front Desk Concierge" },
        {
          label: "Primary role",
          value: "Front-desk guidance and safe intake shaping",
        },
        {
          label: "Primary surfaces",
          value: "Studio builder, internal preview, sandbox rehearsal",
        },
        {
          label: "Runtime posture",
          value: "Control layer only. Live clinic routing remains unchanged.",
        },
      ],
      checklist: [
        {
          label: "Operator brief stays separate from runtime webhook logic",
          state: "done",
        },
        {
          label: "Medical scope remains limited to safe front-desk assistance",
          state: "done",
        },
        {
          label: "Live clinic activation remains blocked from Studio",
          state: "done",
        },
      ],
      callout: {
        title: "Why this step exists",
        body: "Studio captures the build intent first so the later persona, policy, and preview steps stay tied to operator-approved scope instead of ad hoc prompt edits.",
      },
    },
    {
      id: "persona",
      title: "Persona",
      eyebrow: "Voice and tone",
      summary:
        "Define the assistant voice, opening style, and tone guardrails for preview rendering and internal review.",
      state: "ready",
      fields: [
        {
          label: "Voice character",
          value: "Calm, premium, concise, reassuring",
        },
        {
          label: "Opening move",
          value: "Acknowledge the request, then offer the clearest next step",
        },
        {
          label: "Tone guardrails",
          value:
            "Warm and professional without hype, slang, or improvisation",
        },
        {
          label: "Hand-off style",
          value: "Explain the next step clearly before routing to staff",
        },
      ],
      checklist: [
        {
          label: "Warmth stays business-class, not casual or salesy",
          state: "done",
        },
        {
          label: "Professional close remains explicit and polite",
          state: "done",
        },
        {
          label: "Escalation rules still outrank style improvements",
          state: "done",
        },
      ],
    },
    {
      id: "capabilities",
      title: "Capabilities",
      eyebrow: "What the assistant can do",
      summary:
        "Show the approved front-desk tasks the assistant may support while keeping clinical, payment, and webhook scope closed.",
      state: "ready",
      fields: [
        {
          label: "Allowed",
          value:
            "Hours, location, scheduling guidance, renewals framing, staff handoff setup",
        },
        {
          label: "Preview goal",
          value: "Measure phrasing quality and render safe demo responses",
        },
        {
          label: "Out of scope",
          value:
            "Diagnosis, dosage, medical intake expansion, card data collection",
        },
        {
          label: "Runtime action",
          value: "No live webhook or booking behavior changes",
        },
      ],
      checklist: [
        {
          label: "Capabilities stay within front-desk operations",
          state: "done",
        },
        {
          label: "Medical and PHI boundaries remain unchanged",
          state: "done",
        },
        {
          label: "Studio scope does not replace the existing dashboard or controls",
          state: "done",
        },
      ],
    },
    {
      id: "transfer_rules",
      title: "Transfer rules",
      eyebrow: "Staff escalation policy",
      summary:
        "Review the deterministic conditions that force staff takeover and keep live launch-critical channels out of Studio control.",
      state: "ready",
      fields: [
        {
          label: "Immediate transfer triggers",
          value:
            "Medical advice, uncertain eligibility, PHI-heavy requests, unhappy caller",
        },
        {
          label: "Launch-critical channels",
          value:
            "Inbound receptionist, outbound scheduling, outbound general live",
        },
        {
          label: "Studio channels",
          value: "Internal preview, demo driver, sandbox rehearsal",
        },
        {
          label: "Handoff truth",
          value:
            "Studio can describe handoff rules but cannot activate them in live lanes",
        },
      ],
      checklist: [
        {
          label: "Launch-critical clinic channels remain blocked",
          state: "done",
        },
        {
          label: "Escalation rules remain deterministic and visible",
          state: "done",
        },
        {
          label: "Twilio production routing stays untouched",
          state: "done",
        },
      ],
    },
    {
      id: "booking_behavior",
      title: "Booking behavior",
      eyebrow: "Routing truth",
      summary:
        "Keep owner-confirmed Acuity booking truth visible while preparing future assistant behavior around the current launch-safe routing posture.",
      state: "ready",
      fields: [
        {
          label: "Public booking default",
          value: "Acuity-backed Doctor 1 / Doctor 3 pooled routing",
        },
        {
          label: "Excluded from normal launch routing",
          value: "New Patient Online, z, Veterans monthly-event calendar",
        },
        {
          label: "Booking activation",
          value: "Blocked from Studio and blocked from hydration alone",
        },
        {
          label: "Webhook posture",
          value: "Existing Acuity booking and webhook behavior remains unchanged",
        },
      ],
      checklist: [
        {
          label: "Owner-confirmed routing truth stays visible in the builder",
          state: "done",
        },
        {
          label: "Studio cannot widen booking scope or activate new calendars",
          state: "done",
        },
        {
          label: "Clinic launch-safe routing remains the source of truth",
          state: "done",
        },
      ],
    },
    {
      id: "preview",
      title: "Preview",
      eyebrow: "Internal rendering",
      summary:
        "Prepare internal renders and sandbox rehearsals using the existing preview-only provider seam and humanization gates.",
      state: "ready",
      fields: [
        { label: "Execution lane", value: "Internal preview only" },
        {
          label: "Current allowed scenario channels",
          value:
            "internal_preview, demo_driver, sandbox_rehearsal, voice_quality_lab",
        },
        {
          label: "Quality gate",
          value: "A20.6 humanization scoring remains in pass state",
        },
        {
          label: "Provider mode",
          value: "Preview-only ElevenLabs adapter",
        },
      ],
      checklist: [
        {
          label: "Preview renders stay off live clinic traffic",
          state: "done",
        },
        {
          label: "Operator demos reuse isolated preview controls",
          state: "done",
        },
        {
          label: "Preview state is ready for internal rehearsal only",
          state: "review",
        },
      ],
      callout: {
        title: "Preview note",
        body: "Preview is strong enough for internal rehearsal, evidence capture, and founder demos, but it is still not a runtime cutover switch.",
      },
    },
    {
      id: "deploy_readiness",
      title: "Deploy readiness",
      eyebrow: "Readiness and handoff",
      summary:
        "Expose blocker truth, rollout review posture, and internal signoff state so Studio can support install-week walkthroughs without implying public-live activation.",
      state: "blocked",
      fields: [
        {
          label: "Current state",
          value: "Internal launch candidate; public-live still gated",
        },
        {
          label: "Approval status",
          value:
            "Internal signoff and rollout review supported; public rollout remains separate",
        },
        {
          label: "Rollout rule",
          value: "Manifest only. No live activation from Studio.",
        },
        {
          label: "Launch truth",
          value: "Twilio production behavior and clinic routing remain unchanged",
        },
      ],
      checklist: [
        {
          label: "Internal launch-candidate posture is evidence-backed",
          state: "review",
        },
        {
          label: "Live clinic lanes cannot be enabled from this shell",
          state: "blocked",
        },
        {
          label:
            "Public production activation still requires separate operator rollout workflow",
          state: "blocked",
        },
      ],
      callout: {
        title: "Install-week readiness",
        body: "Studio is strong enough for install-week walkthroughs: premium for demos, operator-safe for review, and honest about what is still gated.",
      },
    },
  ];

  return {
    version: "a24.6_v1",
    title: "VetCan Studio",
    subtitle:
      "A premium build layer for defining, previewing, and preparing an AI front-desk assistant while preserving current clinic launch truth.",
    assistant: {
      name: "VetCan Front Desk Concierge",
      lane: "Studio control layer",
      operatorMode: "Persistent local draft workspace",
      summary:
        "Operators can shape persona, capabilities, transfer policy, booking posture, preview behavior, and internal launch-candidate readiness without changing live routing.",
    },
    stepOrder: steps.map((step) => step.id),
    steps,
    controls: [
      {
        label: "Launch control",
        state: "ready",
        value: "Visible",
        detail:
          "Existing launch-control truth remains visible and stays outside Studio activation.",
      },
      {
        label: "Preview lane",
        state: "ready",
        value: "Internal only",
        detail:
          "ElevenLabs preview remains restricted to allowed scenario channels.",
      },
      {
        label: "Install-week posture",
        state: "review",
        value: "Evidence-backed",
        detail:
          "Studio can be treated as an internal launch candidate, but public-live rollout remains separately gated.",
      },
    ],
    liveDeployment: {
      state: "blocked",
      headline: "Live clinic deployment blocked",
      summary:
        "VetCan Studio is an internal build, preview, readiness, and handoff surface. It does not by itself activate inbound receptionist, outbound scheduling, or other live clinic lanes.",
      blockers: [
        "Public production promotion still belongs to the separate operator rollout workflow",
        "Twilio production routing remains owner-confirmed and unchanged",
        "Booking and webhook behavior cannot be modified from Studio",
      ],
    },
    routingTruth: {
      title: "Owner-confirmed routing truth",
      summary:
        "Studio keeps the current Acuity-backed launch-safe routing truth in view so builder work does not drift from production reality.",
      facts: [
        "Acuity-backed public-only booking remains Doctor 1 / Doctor 3 pooled by default.",
        "New Patient Online, z, and the Veterans monthly-event calendar stay out of normal launch routing.",
        "Runtime route activation stays blocked from Studio and from hydration alone.",
      ],
    },
    previewLane: {
      title: "Preview lane",
      summary:
        "Studio keeps the current scenario execution lane visible so operators can distinguish allowed render channels from workspace-only controls.",
      channels: [
        "internal_preview",
        "demo_driver",
        "sandbox_rehearsal",
        "voice_quality_lab",
      ],
    },
    businessUserExperience: {
      title: "Founder-demo clarity",
      summary:
        "Studio helps founders, owners, and operators understand what is visible now, what remains gated, and what moves to the separate operator rollout workflow.",
      visibleNow: [
        "Install-week walkthroughs can show premium value without implying live rollout.",
        "Operators can review preview posture, blocker truth, and handoff readiness in one place.",
        "Export and handoff artifacts stay audit-safe, blocker-aware, and non-live.",
      ],
      gatedNow: [
        "Rollout review and any public-live promotion still require separate approval outside Studio.",
        "Studio still does not activate public-live tenant routing or Twilio production cutover.",
        "Current blocker truth remains attached instead of being hidden by signoff language.",
      ],
      nextMove: [
        "Route rollout review and live deployment decisions through the separate operator rollout workflow.",
        "Use the install-week bundle for stakeholder review, operator alignment, and evidence-backed handoff.",
        "Keep docs, tests, and launch-language aligned with actual runtime truth before any public-live expansion.",
      ],
    },
    launchCandidateCloseout: {
      title: "Install-week closeout",
      summary:
        "VetCan Studio is ready for install-week walkthroughs, operator-safe review, and evidence-backed handoff while public-live deployment remains separately gated.",
      scoreLabel: "Install-week score",
      scoreValue: "92 / 100",
      scoreDetail:
        "High internal readiness for demo, review, and handoff. Remaining points stay gated by public-live rollout controls that still sit outside Studio.",
      proofSummary: [
        "Studio shell, preview center, deploy choreography, export, and publish surfaces are present and coherent.",
        "Export and publish artifacts remain blocker-honest, internal-only, and explicit about non-live boundaries.",
        "Current tests and build can verify the product as a credible internal launch candidate.",
      ],
      knownBlockers: [
        "Studio does not itself promote tenants into public-live deployment.",
        "Twilio production routing and launch-critical live paths remain outside Studio control.",
        "Rollout review and public promotion decisions still require the separate operator rollout workflow.",
      ],
      nextOperatorWorkflow: [
        "Review the internal candidate score and proof summary against the latest green test/build state.",
        "Confirm blocker truth, export wording, and publish wording still match current repo behavior.",
        "Use the separate operator rollout workflow for rollout approval, production routing, and any public-live promotion decision.",
      ],
    },
  };
}
